import dayjs from 'dayjs';
import {
  COURSE_PORTFOLIO_STATUS_OPTIONS,
  COURSE_TYPE_OPTIONS
} from './constants';
import {
  CanAccess,
  CrudFilters,
  HttpError,
  useNotification
} from '@refinedev/core';
import { CorporateReportFilterForm } from './components/CorporateReportFilterForm';
import { DatePicker, Form, Select, Table, Tooltip } from 'antd';
import { ExportButton, TextField, useTable } from '@refinedev/antd';
import { ICoursePortfolio } from 'interfaces/corporate_reports';
import { ListLayout } from 'components/layout/layout/ListLayout';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useState } from 'react';
import { useCorporateCourseOptions } from './hooks/useCorporateCourseOptions';
import { useCorporateReportExport } from './hooks/useCorporateReportExport';
import { validateEnrollmentTypeSelection } from './utils/filters';

interface IFilter {
  by_course_type?: string;
  by_course_id?: string[];
  by_range_date?: [dayjs.Dayjs, dayjs.Dayjs];
  by_status?: string;
}

const REPORT_STORAGE_KEY = 'course_portfolios_id';

const getCourseTypeLabel = (value?: string) => {
  return (
    COURSE_TYPE_OPTIONS.find(option => option.value === value)?.label || '-'
  );
};

const formatDate = (value?: string, format = 'DD/MM/YYYY') => {
  if (!value) {
    return '';
  }

  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.format(format) : value;
};

const formatRating = (value?: number) => {
  if (value === null || value === undefined) {
    return '-';
  }

  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });
};

export const CoursePortfolios = () => {
  const [hasSearched, setHasSearched] = useState(false);
  const notification = useNotification();
  const { RangePicker } = DatePicker;

  const { tableProps, searchFormProps } = useTable<
    ICoursePortfolio,
    HttpError,
    IFilter
  >({
    resource: 'course_portfolios',
    queryOptions: { enabled: hasSearched },
    onSearch: params => {
      const filters: CrudFilters = [];

      filters.push(
        {
          field: 'filter[by_course_type]',
          operator: 'eq',
          value: params.by_course_type
        },
        {
          field: 'filter[by_course_id][]',
          operator: 'in',
          value: params.by_course_id
        },
        {
          field: 'filter[by_range_date][]',
          operator: 'between',
          value: [
            params.by_range_date?.[0]?.format('YYYY-MM-DD'),
            params.by_range_date?.[1]?.format('YYYY-MM-DD')
          ]
        },
        {
          field: 'filter[by_status]',
          operator: 'eq',
          value: params.by_status
        }
      );

      return filters;
    },
    sorters: {
      mode: 'server'
    }
  });
  const selectedEnrollmentType = Form.useWatch(
    'by_course_type',
    searchFormProps.form
  );
  const selectedCourseOption =
    selectedEnrollmentType === 'Course'
      ? 'Courses::Enrollment'
      : selectedEnrollmentType === 'Graduate::Course'
      ? 'Graduate::Courses::Enrollment'
      : undefined;
  const tablePropsWithConditionalData = hasSearched
    ? tableProps
    : { ...tableProps, dataSource: [], loading: false };
  const { courseOptions } = useCorporateCourseOptions(selectedCourseOption);

  useEffect(() => {
    searchFormProps.form?.setFieldValue('by_course_id');
  }, [searchFormProps.form, selectedEnrollmentType]);

  const { reportId, handleExportReport } = useCorporateReportExport<IFilter>({
    storageKey: REPORT_STORAGE_KEY,
    reportPath: '/admin/course_portfolios/report',
    searchFormProps,
    notification,
    buildExportFilters: formValues => formValues
  });

  const validateFilter = useCallback(() => {
    return validateEnrollmentTypeSelection(
      searchFormProps.form?.getFieldValue('by_course_type'),
      () =>
        notification.open?.({
          type: 'error',
          message: 'Modalidade obrigatória',
          description: 'Selecione a modalidade para aplicar o filtro.'
        })
    );
  }, [notification, searchFormProps.form]);

  return (
    <ListLayout
      title="Portfólio de Cursos"
      headerActions={
        <CanAccess resource="corporate_reports/course_portfolios" action="list">
          <ExportButton
            onClick={handleExportReport}
            disabled={!!reportId || !hasSearched || !selectedEnrollmentType}
            loading={!!reportId}
          >
            {!!reportId ? 'Relatório está sendo gerado' : 'Exportar'}
          </ExportButton>
        </CanAccess>
      }
    >
      <CorporateReportFilterForm
        searchFormProps={searchFormProps}
        tableLoading={!!tableProps.loading}
        setHasSearched={setHasSearched}
        onValidateFilter={validateFilter}
      >
        <Form.Item className="mb-0 w-45" help={null}>
          <Form.Item
            name="by_course_type"
            noStyle
            rules={[{ required: true, message: '' }]}
          >
            <Select
              allowClear
              placeholder="Modalidade"
              options={COURSE_TYPE_OPTIONS}
            />
          </Form.Item>
        </Form.Item>

        <Form.Item name="by_course_id" className="mb-0 w-45">
          <Select
            mode="multiple"
            allowClear
            disabled={!selectedEnrollmentType}
            style={{ width: '100%' }}
            placeholder="Curso"
            {...courseOptions}
          />
        </Form.Item>
        <Form.Item name="by_range_date">
          <RangePicker format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item name="by_status" className="mb-0 w-35">
          <Select
            allowClear
            placeholder="Status"
            options={COURSE_PORTFOLIO_STATUS_OPTIONS}
          />
        </Form.Item>
      </CorporateReportFilterForm>

      <Table
        {...tablePropsWithConditionalData}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          ...tablePropsWithConditionalData.pagination,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} itens`
        }}
      >
        <Table.Column
          dataIndex="course_type"
          key="course_type"
          title="Modalidade"
          width={140}
          render={value => <TextField value={getCourseTypeLabel(value)} />}
        />
        <Table.Column
          dataIndex="course_name"
          key="course_name"
          title="Curso"
          sorter
          className="min-w-[120px]"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="duration_in_hours"
          key="duration_in_hours"
          title="Duração (horas)"
          width={140}
          render={value => <TextField value={value ?? '-'} />}
        />
        <Table.Column
          dataIndex="course_kind"
          key="course_kind"
          title="Tipo"
          width={100}
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="start_date"
          key="start_date"
          title="Data de início"
          sorter
          width={150}
          render={value => (
            <TextField value={formatDate(value, 'DD/MM/YYYY')} />
          )}
        />
        <Table.Column
          dataIndex="end_date"
          key="end_date"
          title="Data de término"
          sorter
          width={150}
          render={value => (
            <TextField value={formatDate(value, 'DD/MM/YYYY')} />
          )}
        />
        <Table.Column
          dataIndex="status"
          key="status"
          title="Status"
          width={140}
          render={value => (
            <TextField
              value={
                COURSE_PORTFOLIO_STATUS_OPTIONS.find(
                  option => option.value === value
                )?.label ||
                value ||
                '-'
              }
            />
          )}
        />
        <Table.Column
          dataIndex="rating_average"
          key="rating_average"
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              Média
              <Tooltip title="Média da nota que os alunos atribuíram ao curso">
                <QuestionCircleOutlined />
              </Tooltip>
            </span>
          }
          width={120}
          render={value => <TextField value={formatRating(value)} />}
        />
      </Table>
    </ListLayout>
  );
};
